import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { LOGIN } from "@/constants/testIds/auth";
import "./admin.css";

export default function AdminLogin() {
  const { isAuthenticated, booting, login } = useAdminAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (!booting && isAuthenticated) return <Navigate to="/admin/brokers" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username.trim(), password);
      navigate("/admin/brokers", { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-shell admin-grid-bg flex min-h-screen items-center justify-center px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
        className="admin-panel w-full max-w-[440px] p-8 shadow-[0_0_0_1px_rgba(255,255,255,0.03)]"
      >
        <div className="mb-8 flex items-start gap-3">
          <div className="admin-brand-mark"><LockKeyhole size={16} /></div>
          <div>
            <p className="admin-mono text-[11px] text-[#64748b]">Secure console</p>
            <h1 className="mt-1 text-[28px] font-light tracking-[-0.04em] text-white">Admin sign in</h1>
            <p className="mt-2 text-sm leading-relaxed text-[#94a3b8]">
              Restricted access for broker catalog operations. Sessions expire after 12 hours.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <label className="block">
            <span className="mb-1.5 block text-xs text-[#94a3b8]">Username</span>
            <input
              className="admin-input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid={LOGIN.emailInput}
              required
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs text-[#94a3b8]">Password</span>
            <input
              type="password"
              className="admin-input"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={LOGIN.passwordInput}
              required
            />
          </label>

          {error ? (
            <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="admin-btn admin-btn-primary w-full" disabled={busy} data-testid={LOGIN.submitButton}>
            {busy ? "Verifying…" : "Enter console"}
          </button>
        </form>

        <div className="mt-6 flex items-center gap-2 text-xs text-[#64748b]">
          <ShieldCheck size={14} className="text-[#0052ff]" />
          JWT-protected routes · Bearer token never logged
        </div>
      </motion.div>
    </div>
  );
}
